import { Component, Inject, TemplateRef, ViewChild } from '@angular/core';

import { LiveNavigationConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { LiveIndicatorType } from '@frontend/sports/types/components/features';
import { Store } from '@ngrx/store';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';
import { EachWayMarketHelperService } from 'packages/sports/web/app/src/option-group/each-way-market-helper.service';
import { uiStateSelector } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { Observable, combineLatest, map } from 'rxjs';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipV2OptionMarketPick } from '../../../../core/picks/betslip-v2-option-market-pick';
import { IBetslipV2Option, IBetslipV2PickWinParticipantMarket, PickOddsState } from '../../../../core/picks/pick-models';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2HorseRaceWinParticipantPick,
    BetslipV2OptionMarketXCastRacePick,
} from '../../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { BetslipMarketSubTypeLogoService } from '../../services/betslip-market-sub-type-logo.service';

@Component({
    templateUrl: './pick-display-templates.component.html',
    standalone: false,
})
export default class PickDisplayTemplatesComponent {
    constructor(
        public sitecore: Sitecore,
        private eachWayMarketHelper: EachWayMarketHelperService,
        private epcotConfigService: EpcotConfigService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) private messageProvider: IBetslipMessagesProviderService,
        private betslipMarketSubTypeLogoService: BetslipMarketSubTypeLogoService,
        private store: Store<IBetslipRootState>,
        private liveNavConfig: LiveNavigationConfig,
    ) {
        this.viewState$ = combineLatest([
            this.store.select(selectIsLinearBetslip),
            this.store.select(uiStateSelector).pipe(map((uiState) => uiState.states.some((s) => s === UiStates.EditBet))),
        ]).pipe(
            map(([isLinear, hasEditBetState]) => {
                return {
                    isLinear,
                    hasEditBetState,
                };
            }),
        );
    }

    @ViewChild('betBuilderPickTemplate', { read: TemplateRef, static: true }) betBuilderPickTemplate: TemplateRef<any>;
    @ViewChild('groupedPickTemplate', { read: TemplateRef, static: true }) groupedPickTemplate: TemplateRef<any>;
    @ViewChild('participantPickTemplate', { read: TemplateRef, static: true }) participantPickTemplate: TemplateRef<any>;
    @ViewChild('optionMarketPickTemplate', { read: TemplateRef, static: true }) optionMarketPickTemplate: TemplateRef<any>;
    @ViewChild('unknownPickTemplate', { read: TemplateRef, static: true }) unknownPickTemplate: TemplateRef<any>;
    isEpcotEnabled = this.epcotConfigService.isEnabled();
    isBadgeIndicator = this.liveNavConfig.liveIndicatorType === LiveIndicatorType.Badge;
    private viewState$: Observable<{ isLinear: boolean; hasEditBetState: boolean }>;

    marketSubTypeLogoTitle$(pick: BetslipPick, betslipType?: BetslipType): Observable<string | undefined> {
        return this.viewState$.pipe(
            map((viewState) => {
                const showMarketSubTypeLogoTitle = viewState.isLinear ? betslipType === BetslipType.Single : true;

                return showMarketSubTypeLogoTitle && !viewState.hasEditBetState
                    ? this.betslipMarketSubTypeLogoService.getMarketSubTypeLogoTitle(pick)
                    : undefined;
            }),
        );
    }

    marketSubTypeSignpostingText$(pick: BetslipPick, betslipType?: BetslipType): Observable<string | undefined> {
        return this.viewState$.pipe(
            map((viewState) => {
                const showSignpostingText = viewState.isLinear
                    ? betslipType && betslipType !== BetslipType.Single && pick.oddsState === PickOddsState.Open
                    : false;

                return showSignpostingText && !viewState.hasEditBetState
                    ? this.betslipMarketSubTypeLogoService.getMarketSubTypeSignpostingText(pick)
                    : undefined;
            }),
        );
    }

    optionMarketName(pick: BetslipPick, isEachWay: boolean): string {
        if (
            !isEachWay &&
            ((BetslipV2HorseRaceOptionMarketPick.isPick(pick) && pick.market.marketType?.toString() === RacingOptionMarketTypes.WinEw) ||
                (BetslipV2OptionMarketXCastRacePick.isPick(pick) &&
                    (pick.market.marketType?.toString() === RacingOptionMarketTypes.Forecast ||
                        pick.market.marketType?.toString() === RacingOptionMarketTypes.Tricast)))
        ) {
            return pick.betType;
        }
        const isEWExtraPlace = pick.market.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace;
        if (isEachWay && BetslipV2OptionMarketPick.isPick(pick) && pick.market.isEachWay && !isEWExtraPlace) {
            return this.messageProvider.getMessages().common.EachWay;
        }

        if (isEWExtraPlace) {
            const market = pick.market as IBetslipV2PickWinParticipantMarket;
            const eachWayExtra = this.eachWayMarketHelper.calculateEachWayTerms(market.placeTerms!);

            return pick.marketName.name === RacingOptionMarketTypes.EachWay ? `${pick.marketName.name} ${eachWayExtra}` : pick.marketName.name;
        }

        if (pick.boostedPriceDetails?.isPriceBoosted) {
            return `${pick.marketName.name} - ${this.sitecore.event.PriceBoostMarket}`;
        }

        return pick.marketName.name;
    }

    isHorseEachWayPick(pick: BetslipPick): boolean {
        return pick instanceof BetslipV2HorseRaceWinParticipantPick;
    }

    showBABEditButton(pick: BetslipBetBuilderPick): boolean {
        return BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick) || !!pick.longIds?.length;
    }

    betBuilderEditPickButtonClass(pick: BetslipBetBuilderPick): string {
        return BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick) ? 'betslip-digital-pick__line-2' : 'betslip-digital-pick__line-1';
    }

    isHorseOptionMarketAnycastPick(pick: BetslipPick): boolean {
        return BetslipV2OptionMarketXCastRacePick.isPick(pick);
    }

    isHorseRaceOptionPick(pick: BetslipPick): boolean {
        return pick instanceof BetslipV2HorseRaceOptionMarketPick || pick instanceof BetslipV2OptionMarketXCastRacePick;
    }

    getSortedPickOptions(pick: BetslipV2OptionMarketXCastRacePick): IBetslipV2Option[] {
        return pick.options.sort((a, b) => a.position! - b.position!);
    }
}
